import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Download, ChevronLeft, ChevronRight, User as UserIcon } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { listPersonas, type Persona } from '../api';

/* ---------- export helpers ---------- */

function downloadJson(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadCsv(personas: Persona[]) {
  const headers = ['slug', 'title', 'buying_role', 'photo_url', 'nocodb_id'];
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const rows = [headers.join(',')];
  for (const p of personas) {
    rows.push(headers.map((h) => esc((p as unknown as Record<string, unknown>)[h])).join(','));
  }
  const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'atek-personas.csv';
  a.click();
  URL.revokeObjectURL(url);
}

/* ---------- detail rendering ---------- */

// Order of detail sections to render (matches n8n Buyer Persona shape).
const DETAIL_SECTIONS: string[] = [
  'Basic Information',
  'Demographics',
  'Professional Profile',
  'Pain Points & Challenges',
  'Goals & Motivations',
  'Real Quotes',
  'Decision Triggers',
  'Objections',
  'ATEK Solution Alignment',
  'Approach Strategy',
  'Communication Preferences',
  'Buying Behavior',
  'Technology Profile',
  'Relationship with ATEK',
  'Email Sequence',
  'Lead Magnet',
];

/** Tailwind-styled markdown renderer for persona sections. */
const MARKDOWN_COMPONENTS: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  h1: ({ children }) => <h2 className="text-xl font-semibold text-slate-900 mt-4 mb-2">{children}</h2>,
  h2: ({ children }) => <h3 className="text-lg font-semibold text-slate-900 mt-3 mb-2">{children}</h3>,
  h3: ({ children }) => <h4 className="text-base font-semibold text-slate-900 mt-3 mb-1.5">{children}</h4>,
  h4: ({ children }) => <h5 className="text-sm font-semibold text-slate-800 mt-2 mb-1">{children}</h5>,
  p:  ({ children }) => <p className="text-sm text-slate-800 leading-relaxed mb-2">{children}</p>,
  ul: ({ children }) => <ul className="list-disc ml-5 space-y-1 text-sm text-slate-800 mb-2">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal ml-5 space-y-1 text-sm text-slate-800 mb-2">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="italic text-slate-700 border-l-4 border-[#5A1DEF] pl-4 py-1 my-2">{children}</blockquote>
  ),
  code: ({ children, className }) => {
    const inline = !className?.includes('language-');
    return inline
      ? <code className="bg-slate-100 px-1 py-0.5 rounded text-[0.85em] text-slate-800">{children}</code>
      : <code className={className}>{children}</code>;
  },
  pre: ({ children }) => (
    <pre className="bg-slate-50 border rounded p-3 overflow-x-auto text-xs my-2">{children}</pre>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-3">
      <table className="min-w-full text-sm border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-50">{children}</thead>,
  th: ({ children }) => <th className="border border-slate-200 px-3 py-1.5 text-left font-semibold text-slate-700">{children}</th>,
  td: ({ children }) => <td className="border border-slate-200 px-3 py-1.5 align-top text-slate-800">{children}</td>,
  strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#5A1DEF] underline hover:opacity-80">
      {children}
    </a>
  ),
  hr: () => <hr className="my-3 border-slate-200" />,
};

function Markdown({ content }: { content: string }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
      {content}
    </ReactMarkdown>
  );
}

function DetailSection({ title, content }: { title: string; content: unknown }) {
  if (content == null) return null;
  if (typeof content !== 'string') {
    return (
      <section className="border-t pt-3">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{title}</h4>
        <pre className="text-xs text-slate-700 bg-slate-50 p-2 rounded overflow-x-auto">
          {JSON.stringify(content, null, 2)}
        </pre>
      </section>
    );
  }
  if (!content.trim()) return null;
  return (
    <section className="border-t pt-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{title}</h4>
      <Markdown content={content} />
    </section>
  );
}

function PersonaDetail({ persona, index, total, onPrev, onNext }: {
  persona: Persona;
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <article className="border rounded-lg bg-white p-5 space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-4 min-w-0">
          {persona.photo_url ? (
            <img
              src={persona.photo_url}
              alt={persona.name ?? persona.title}
              className="w-20 h-20 rounded-full object-cover border shrink-0"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-slate-100 border flex items-center justify-center shrink-0">
              <UserIcon className="h-8 w-8 text-slate-400" />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Persona {index + 1} of {total}
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mt-0.5 truncate">
              {persona.name ?? persona.title}
            </h3>
            {persona.buying_role && (
              <p className="text-sm text-slate-600 mt-0.5">{persona.buying_role}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button size="icon" variant="outline" onClick={onPrev} aria-label="Previous"><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="icon" variant="outline" onClick={onNext} aria-label="Next"><ChevronRight className="h-4 w-4" /></Button>
          <Button
            size="sm" variant="outline" className="ml-2 gap-1.5"
            onClick={() => downloadJson(persona, `atek-persona-${persona.slug}.json`)}
          >
            <Download className="h-3.5 w-3.5" /> JSON
          </Button>
        </div>
      </header>

      {DETAIL_SECTIONS.map((k) => (
        <DetailSection key={k} title={k} content={(persona.details as Record<string, unknown>)[k]} />
      ))}
    </article>
  );
}

/* ---------- page ---------- */

export function PersonasTab() {
  const { data: personas, isLoading, error } = useQuery({
    queryKey: ['brand-personas'],
    queryFn: listPersonas,
    staleTime: 5 * 60 * 1000,
  });

  const [search, setSearch] = useState('');
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);

  const all = personas ?? [];
  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((p) =>
      (p.name || p.title).toLowerCase().includes(q) ||
      (p.buying_role || '').toLowerCase().includes(q),
    );
  }, [all, search]);

  const selectedIndex = useMemo(() => {
    if (!selectedSlug) return 0;
    const i = list.findIndex((p) => p.slug === selectedSlug);
    return i === -1 ? 0 : i;
  }, [list, selectedSlug]);

  const current = list[selectedIndex];

  const goTo = (offset: number) => {
    if (list.length === 0) return;
    const next = (selectedIndex + offset + list.length) % list.length;
    setSelectedSlug(list[next].slug);
  };

  if (isLoading) return <Skeleton className="h-[400px] w-full" />;
  if (error) {
    return (
      <div className="border border-red-200 bg-red-50 text-red-800 rounded p-3 text-sm">
        Failed to load personas: {error instanceof Error ? error.message : String(error)}
      </div>
    );
  }
  if (all.length === 0) {
    return (
      <div className="border border-dashed rounded-lg p-8 text-center text-sm text-slate-500">
        No personas yet. Seed via{' '}
        <code className="bg-slate-100 px-1 rounded">bun apps/rest_server/scripts/import-personas-from-n8n.js --execute</code>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,300px)_1fr] gap-4 items-start">
      {/* Sidebar */}
      <aside className="border rounded-lg bg-white overflow-hidden">
        <div className="px-3 py-2 border-b bg-slate-50 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            Personas ({list.length}{list.length !== all.length ? ` / ${all.length}` : ''})
          </span>
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" className="h-6 w-6" title="Export JSON"
              onClick={() => { downloadJson({ personas: list }, 'atek-personas.json'); toast.success('Downloaded'); }}>
              <Download className="h-3.5 w-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" title="Export CSV"
              onClick={() => { downloadCsv(list); toast.success('Downloaded'); }}>
              <span className="text-[9px] font-bold">CSV</span>
            </Button>
          </div>
        </div>
        <div className="px-3 py-2 border-b">
          <Input
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-xs"
          />
        </div>
        <ul className="max-h-[70vh] overflow-y-auto">
          {list.map((p) => {
            const active = p.slug === current?.slug;
            return (
              <li key={p.id}>
                <button
                  onClick={() => setSelectedSlug(p.slug)}
                  className={`w-full text-left px-3 py-2 border-b last:border-b-0 transition-colors flex items-center gap-2.5 ${
                    active ? 'bg-[#5A1DEF]/10' : 'hover:bg-slate-50'
                  }`}
                >
                  {p.photo_url ? (
                    <img
                      src={p.photo_url}
                      alt=""
                      loading="lazy"
                      className="h-9 w-9 rounded-full object-cover border shrink-0"
                    />
                  ) : (
                    <div className="h-9 w-9 rounded-full bg-slate-100 border flex items-center justify-center shrink-0">
                      <UserIcon className="h-4 w-4 text-slate-400" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className={`text-sm line-clamp-1 ${active ? 'text-[#5A1DEF] font-medium' : 'text-slate-900'}`}>
                      {p.name ?? p.title}
                    </div>
                    {p.buying_role && (
                      <div className="text-[10px] text-slate-500 line-clamp-1">{p.buying_role}</div>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      {current && (
        <PersonaDetail
          persona={current}
          index={selectedIndex}
          total={list.length}
          onPrev={() => goTo(-1)}
          onNext={() => goTo(1)}
        />
      )}
    </div>
  );
}
